import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
  FileText,
  Download,
  ArrowLeft,
  Stamp,
  Receipt,
  Building,
  BriefcaseIcon,
  User,
  Mail,
  Phone,
  Clock,
  Languages,
  DollarSign,
  MapPin,
  CheckCircle,
  CreditCard,
  Globe,
  Users,
} from "lucide-react";
import Header from "../../Header";
import { ServiceStatusBadge, ServiceStatusUpdate } from "./ServiceStatusUpdate";
import documentHandler from "../../../utils/documentHandler";
import ServiceDocumentUploader from "../../ServiceDocumentUploader";

const ServiceDetailView = () => {
  const { id, serviceId } = useParams();
  const navigate = useNavigate();
  const [service, setService] = useState(null);
  const [loading, setLoading] = useState(true);
  const [notification, setNotification] = useState({
    show: false,
    message: "",
    type: "",
  });

  const fetchService = async () => {
    try {
      // If we have both company ID and service ID, use the company-specific endpoint
      if (id) {
        const response = await fetch(
          `https://api.instacorp.ae/api/admin/data/companies/${id}/services/${serviceId}`,
          {
            headers: {
              Authorization: `Bearer ${localStorage.getItem("token")}`,
            },
          }
        );
        if (!response.ok) throw new Error("Failed to fetch service");
        const data = await response.json();
        setService(data);
      } else {
        // If we only have service ID, use a standalone service endpoint
        const response = await fetch(
          `https://api.instacorp.ae/api/admin/data/services/${serviceId}`,
          {
            headers: {
              Authorization: `Bearer ${localStorage.getItem("token")}`,
            },
          }
        );
        if (!response.ok) throw new Error("Failed to fetch service");
        const data = await response.json();
        setService(data);
      }
    } catch (error) {
      console.error("Error fetching service:", error);
      setNotification({
        show: true,
        message: "Failed to load service details",
        type: "error",
      });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchService();
  // eslint-disable-next-line react-hooks/exhaustive-deps -- fetchService is stable, id/serviceId are the triggers
  }, [id, serviceId]);

  const handleStatusUpdate = async () => {
    await fetchService();
    setNotification({
      show: true,
      message: "Service status updated successfully",
      type: "success",
    });
    setTimeout(() => setNotification({ show: false, message: "", type: "" }), 3000);
  };

  const handleDocumentUpload = async (uploadResult) => {
    // Refresh the service to show the new document
    await fetchService();
    setNotification({
      show: true,
      message: "Document uploaded successfully",
      type: "success",
    });
    setTimeout(() => setNotification({ show: false, message: "", type: "" }), 3000);
  };

  const handleDocumentDelete = async (fileUrl) => {
    // Refresh the service to remove the deleted document
    await fetchService();
    setNotification({
      show: true,
      message: "Document deleted successfully",
      type: "success",
    });
    setTimeout(() => setNotification({ show: false, message: "", type: "" }), 3000);
  };

  const handleServiceStatusUpdate = async (action) => {
    // Refresh the service to get updated status
    await fetchService();
    setNotification({
      show: true,
      message: `Service status updated after ${action}`,
      type: "success",
    });
    setTimeout(() => setNotification({ show: false, message: "", type: "" }), 3000);
  };

  const getServiceIcon = (serviceType) => {
    switch (serviceType) {
      case "Company Stamp":
        return Stamp;
      case "Corporate Tax":
        return Receipt;
      case "Bank Account Opening":
        return Building;
      case "Legal Translation":
        return Languages;
      case "GO AML":
        return FileText;
      case "VAT Registration":
        return Receipt;
      case "Medical Insurance":
        return CreditCard;
      case "MOFA Attestation":
        return Globe;
      case "P.O. Box Registration":
        return Mail;
      case "P.O. Box Registration and Company Stamp Offer":
        return Mail;
      default:
        if (serviceType?.startsWith("Visa Application")) return Users;
        return BriefcaseIcon;
    }
  };

  const renderServiceSpecificDetails = () => {
    if (!service) return null;

    switch (service.serviceType) {
      case "Company Stamp":
        return (
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <h4 className="text-sm font-medium text-gray-500">Emirate</h4>
                <p className="mt-1 text-sm text-gray-900">{service.emirate}</p>
              </div>
              <div>
                <h4 className="text-sm font-medium text-gray-500">Delivery Method</h4>
                <p className="mt-1 text-sm text-gray-900 capitalize">{service.deliveryMethod}</p>
              </div>
            </div>
            {service.deliveryMethod === "delivery" && (
              <div>
                <h4 className="text-sm font-medium text-gray-500">Delivery Address</h4>
                <p className="mt-1 text-sm text-gray-900 whitespace-pre-line">
                  {service.deliveryAddress}
                </p>
              </div>
            )}
          </div>
        );

      case "Corporate Tax":
        return (
          <div className="grid grid-cols-2 gap-4">
            <div>
              <h4 className="text-sm font-medium text-gray-500">Revenue Threshold</h4>
              <p className="mt-1 text-sm text-gray-900">
                {service.revenueThreshold === "true" ? "Yes" : "No"}
              </p>
            </div>
            <div>
              <h4 className="text-sm font-medium text-gray-500">UAE Residence Visa</h4>
              <p className="mt-1 text-sm text-gray-900">
                {service.hasResidenceVisa === "true" ? "Yes" : "No"}
              </p>
            </div>
            <div>
              <h4 className="text-sm font-medium text-gray-500">Exclusions</h4>
              <p className="mt-1 text-sm text-gray-900">
                {service.exclusions === "true" ? "Yes" : "No"}
              </p>
            </div>
          </div>
        );

      case "Bank Account Opening":
        return (
          <div className="space-y-6">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <h4 className="text-sm font-medium text-gray-500">Bank Plan</h4>
                <p className="mt-1 text-sm text-gray-900">{service.bankPlan}</p>
              </div>
              <div>
                <h4 className="text-sm font-medium text-gray-500">Account Type</h4>
                <p className="mt-1 text-sm text-gray-900">{service.accountType}</p>
              </div>
              <div>
                <h4 className="text-sm font-medium text-gray-500">PO Box</h4>
                <p className="mt-1 text-sm text-gray-900">{service.poBox || "Not provided"}</p>
              </div>
            </div>

            {service.shareholders && service.shareholders.length > 0 && (
              <div>
                <h4 className="text-base font-medium text-gray-900 mb-3">Shareholders</h4>
                <div className="space-y-4">
                  {service.shareholders.map((shareholder, index) => (
                    <div key={index} className="p-4 bg-gray-50 rounded-lg">
                      <div className="grid grid-cols-2 gap-4">
                        <div>
                          <h5 className="text-sm font-medium text-gray-500">Name</h5>
                          <p className="text-sm text-gray-900">{shareholder.name}</p>
                        </div>
                        <div>
                          <h5 className="text-sm font-medium text-gray-500">Email</h5>
                          <p className="text-sm text-gray-900">{shareholder.email}</p>
                        </div>
                        <div>
                          <h5 className="text-sm font-medium text-gray-500">Mobile</h5>
                          <p className="text-sm text-gray-900">{shareholder.mobile}</p>
                        </div>
                        <div>
                          <h5 className="text-sm font-medium text-gray-500">Address</h5>
                          <p className="text-sm text-gray-900">{shareholder.address}</p>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        );

      case "Legal Translation":
        return (
          <div className="space-y-6">
            {/* Basic Information */}
            <div className="grid grid-cols-2 gap-4">
              <div>
                <h4 className="text-sm font-medium text-gray-500">Document Origin</h4>
                <p className="mt-1 text-sm text-gray-900">{service.documentOrigin || "N/A"}</p>
              </div>
              <div>
                <h4 className="text-sm font-medium text-gray-500">Number of Documents</h4>
                <p className="mt-1 text-sm text-gray-900">{service.numberOfDocuments || 0}</p>
              </div>
              <div>
                <h4 className="text-sm font-medium text-gray-500">Pickup Method</h4>
                <p className="mt-1 text-sm text-gray-900 capitalize">{service.pickupMethod || "N/A"}</p>
              </div>
              <div>
                <h4 className="text-sm font-medium text-gray-500">Delivery Method</h4>
                <p className="mt-1 text-sm text-gray-900 capitalize">{service.deliveryMethod || "N/A"}</p>
              </div>
            </div>

            {/* MoFA Attestation */}
            <div className="flex items-center space-x-2">
              <CheckCircle className="h-5 w-5 text-gray-400" />
              <h4 className="text-sm font-medium text-gray-500">MoFA Attestation</h4>
              <span className={`px-2.5 py-0.5 rounded-full text-xs font-medium ${
                service.isAttestedByMoFA === true ? 'bg-green-100 text-green-800' :
                service.isAttestedByMoFA === false ? 'bg-red-100 text-red-800' :
                'bg-gray-100 text-gray-800'
              }`}>
                {service.isAttestedByMoFA === true ? 'Yes' :
                 service.isAttestedByMoFA === false ? 'No' : 'Not Specified'}
              </span>
            </div>

            {/* Cost Breakdown */}
            {service.cost && (
              <div className="bg-gray-50 rounded-lg p-4">
                <h4 className="text-base font-medium text-gray-900 mb-3 flex items-center">
                  <DollarSign className="h-5 w-5 mr-2" />
                  Cost Breakdown
                </h4>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <h5 className="text-sm font-medium text-gray-500">Translation Cost</h5>
                    <p className="mt-1 text-sm text-gray-900">AED {service.cost.translation?.toLocaleString() || 0}</p>
                  </div>
                  <div>
                    <h5 className="text-sm font-medium text-gray-500">Pickup Cost</h5>
                    <p className="mt-1 text-sm text-gray-900">AED {service.cost.pickup?.toLocaleString() || 0}</p>
                  </div>
                  <div>
                    <h5 className="text-sm font-medium text-gray-500">Delivery Cost</h5>
                    <p className="mt-1 text-sm text-gray-900">AED {service.cost.delivery?.toLocaleString() || 0}</p>
                  </div>
                  <div>
                    <h5 className="text-sm font-medium text-gray-500">Total Cost</h5>
                    <p className="mt-1 text-sm font-bold text-blue-600">AED {service.cost.total?.toLocaleString() || 0}</p>
                  </div>
                </div>
              </div>
            )}

            {/* Delivery Address */}
            {service.deliveryAddress && (
              <div className="flex items-start space-x-2">
                <MapPin className="h-5 w-5 text-gray-400 mt-0.5" />
                <div>
                  <h4 className="text-sm font-medium text-gray-500">Delivery Address</h4>
                  <p className="mt-1 text-sm text-gray-900 whitespace-pre-line">{service.deliveryAddress}</p>
                </div>
              </div>
            )}

            {/* Pickup Address */}
            {service.pickupAddress && (
              <div className="flex items-start space-x-2">
                <MapPin className="h-5 w-5 text-gray-400 mt-0.5" />
                <div>
                  <h4 className="text-sm font-medium text-gray-500">Pickup Address</h4>
                  <p className="mt-1 text-sm text-gray-900 whitespace-pre-line">{service.pickupAddress}</p>
                </div>
              </div>
            )}
          </div>
        );

      case "GO AML":
        return (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <h4 className="text-sm font-medium text-gray-500">P.O. Box</h4>
              <p className="mt-1 text-sm text-gray-900">{service.poBox || "N/A"}</p>
            </div>
            <div>
              <h4 className="text-sm font-medium text-gray-500">Email</h4>
              <p className="mt-1 text-sm text-gray-900">{service.emailId || "N/A"}</p>
            </div>
            <div>
              <h4 className="text-sm font-medium text-gray-500">Mobile Number</h4>
              <p className="mt-1 text-sm text-gray-900">{service.mobileNumber || "N/A"}</p>
            </div>
            <div>
              <h4 className="text-sm font-medium text-gray-500">Home Address</h4>
              <p className="mt-1 text-sm text-gray-900">{service.homeAddress || "N/A"}</p>
            </div>
          </div>
        );

      case "VAT Registration":
        return (
          <div className="space-y-6">
            {/* Contact Details */}
            {service.contactDetails && (
              <div>
                <h4 className="text-base font-medium text-gray-900 mb-3">Contact Details</h4>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <h5 className="text-sm font-medium text-gray-500">Mobile</h5>
                    <p className="mt-1 text-sm text-gray-900">{service.contactDetails.mobile || "N/A"}</p>
                  </div>
                  <div>
                    <h5 className="text-sm font-medium text-gray-500">Email</h5>
                    <p className="mt-1 text-sm text-gray-900">{service.contactDetails.email || "N/A"}</p>
                  </div>
                </div>
              </div>
            )}

            {/* Bank Details */}
            {service.bankDetails && (
              <div>
                <h4 className="text-base font-medium text-gray-900 mb-3">Bank Details</h4>
                <div className="grid grid-cols-2 gap-4">
                  {Object.entries(service.bankDetails).map(([key, value]) => (
                    <div key={key}>
                      <h5 className="text-sm font-medium text-gray-500">
                        {key.replace(/([A-Z])/g, " $1").replace(/^./, (s) => s.toUpperCase())}
                      </h5>
                      <p className="mt-1 text-sm text-gray-900">{String(value) || "N/A"}</p>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Office Details */}
            {service.officeDetails && (
              <div>
                <h4 className="text-base font-medium text-gray-900 mb-3">Office Details</h4>
                <div className="grid grid-cols-2 gap-4">
                  {Object.entries(service.officeDetails).map(([key, value]) => (
                    <div key={key}>
                      <h5 className="text-sm font-medium text-gray-500">
                        {key.replace(/([A-Z])/g, " $1").replace(/^./, (s) => s.toUpperCase())}
                      </h5>
                      <p className="mt-1 text-sm text-gray-900">{String(value) || "N/A"}</p>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Shareholder Documents */}
            {service.shareholderDocuments?.shareholders && service.shareholderDocuments.shareholders.length > 0 && (
              <div>
                <h4 className="text-base font-medium text-gray-900 mb-3">Shareholder Documents</h4>
                <div className="space-y-4">
                  {service.shareholderDocuments.shareholders.map((shareholder, index) => (
                    <div key={index} className="p-4 bg-gray-50 rounded-lg">
                      <h5 className="text-sm font-medium text-gray-900 mb-2">Shareholder {index + 1}</h5>
                      <div className="space-y-2">
                        {["passportCopy", "emiratesId", "visaCopy"].map((docType) => {
                          const doc = shareholder[docType];
                          if (!doc?.url) return null;
                          return (
                            <a
                              key={docType}
                              href={doc.url}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="flex items-center text-sm text-blue-600 hover:underline"
                            >
                              <FileText className="h-4 w-4 mr-2" />
                              {docType.replace(/([A-Z])/g, " $1").replace(/^./, (s) => s.toUpperCase())}
                              {doc.originalName && <span className="text-gray-400 ml-2">({doc.originalName})</span>}
                            </a>
                          );
                        })}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        );

      case "Medical Insurance":
        return (
          <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
            <div>
              <h4 className="text-sm font-medium text-gray-500">Location</h4>
              <p className="mt-1 text-sm text-gray-900">{service.location || "N/A"}</p>
            </div>
            <div>
              <h4 className="text-sm font-medium text-gray-500">Plan Type</h4>
              <p className="mt-1 text-sm text-gray-900">{service.planType || "N/A"}</p>
            </div>
            <div>
              <h4 className="text-sm font-medium text-gray-500">Salary</h4>
              <p className="mt-1 text-sm text-gray-900">
                {service.salary != null ? `AED ${Number(service.salary).toLocaleString()}` : "N/A"}
              </p>
            </div>
            <div>
              <h4 className="text-sm font-medium text-gray-500">Dependent Type</h4>
              <p className="mt-1 text-sm text-gray-900">{service.dependentType || "N/A"}</p>
            </div>
            <div>
              <h4 className="text-sm font-medium text-gray-500">Age</h4>
              <p className="mt-1 text-sm text-gray-900">{service.age || "N/A"}</p>
            </div>
            <div>
              <h4 className="text-sm font-medium text-gray-500">Premium</h4>
              <p className="mt-1 text-sm font-semibold text-blue-600">
                {service.premium != null ? `AED ${Number(service.premium).toLocaleString()}` : "N/A"}
              </p>
            </div>
          </div>
        );

      case "MOFA Attestation":
        return (
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <h4 className="text-sm font-medium text-gray-500">Document Type</h4>
                <p className="mt-1 text-sm text-gray-900">{service.documentType || "N/A"}</p>
              </div>
              <div>
                <h4 className="text-sm font-medium text-gray-500">Origin of Documents</h4>
                <p className="mt-1 text-sm text-gray-900">{service.originOfDocuments || "N/A"}</p>
              </div>
              <div>
                <h4 className="text-sm font-medium text-gray-500">Number of Documents</h4>
                <p className="mt-1 text-sm text-gray-900">{service.numberOfDocuments || "N/A"}</p>
              </div>
              {service.countryOfOrigin && (
                <div>
                  <h4 className="text-sm font-medium text-gray-500">Country of Origin</h4>
                  <p className="mt-1 text-sm text-gray-900">{service.countryOfOrigin}</p>
                </div>
              )}
            </div>

            {/* Attestation Status */}
            <div className="flex flex-wrap gap-4">
              {service.isAttestedInUAE != null && (
                <div className="flex items-center space-x-2">
                  <CheckCircle className={`h-5 w-5 ${service.isAttestedInUAE ? "text-green-500" : "text-gray-300"}`} />
                  <span className="text-sm text-gray-700">Attested in UAE</span>
                </div>
              )}
              {service.isAttestedByForeignMinistry != null && (
                <div className="flex items-center space-x-2">
                  <CheckCircle className={`h-5 w-5 ${service.isAttestedByForeignMinistry ? "text-green-500" : "text-gray-300"}`} />
                  <span className="text-sm text-gray-700">Attested by Foreign Ministry</span>
                </div>
              )}
              {service.isAttestedByUAEEmbassy != null && (
                <div className="flex items-center space-x-2">
                  <CheckCircle className={`h-5 w-5 ${service.isAttestedByUAEEmbassy ? "text-green-500" : "text-gray-300"}`} />
                  <span className="text-sm text-gray-700">Attested by UAE Embassy</span>
                </div>
              )}
            </div>
          </div>
        );

      case "P.O. Box Registration":
        return (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <h4 className="text-sm font-medium text-gray-500">Request Option</h4>
              <p className="mt-1 text-sm text-gray-900">{service.requestOption || "N/A"}</p>
            </div>
            <div>
              <h4 className="text-sm font-medium text-gray-500">Email</h4>
              <p className="mt-1 text-sm text-gray-900">{service.emailId || "N/A"}</p>
            </div>
            <div>
              <h4 className="text-sm font-medium text-gray-500">Mobile Number</h4>
              <p className="mt-1 text-sm text-gray-900">{service.mobileNumber || "N/A"}</p>
            </div>
            <div>
              <h4 className="text-sm font-medium text-gray-500">Home Address</h4>
              <p className="mt-1 text-sm text-gray-900">{service.homeAddress || "N/A"}</p>
            </div>
            <div>
              <h4 className="text-sm font-medium text-gray-500">Building No.</h4>
              <p className="mt-1 text-sm text-gray-900">{service.buildingNo || "N/A"}</p>
            </div>
          </div>
        );

      case "P.O. Box Registration and Company Stamp Offer":
        return (
          <div className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <h4 className="text-sm font-medium text-gray-500">Request Option</h4>
                <p className="mt-1 text-sm text-gray-900">{service.requestOption || "N/A"}</p>
              </div>
              <div>
                <h4 className="text-sm font-medium text-gray-500">Email</h4>
                <p className="mt-1 text-sm text-gray-900">{service.emailId || "N/A"}</p>
              </div>
              <div>
                <h4 className="text-sm font-medium text-gray-500">Mobile Number</h4>
                <p className="mt-1 text-sm text-gray-900">{service.mobileNumber || "N/A"}</p>
              </div>
              <div>
                <h4 className="text-sm font-medium text-gray-500">Building No.</h4>
                <p className="mt-1 text-sm text-gray-900">{service.buildingNo || "N/A"}</p>
              </div>
              <div>
                <h4 className="text-sm font-medium text-gray-500">Delivery Method</h4>
                <p className="mt-1 text-sm text-gray-900 capitalize">{service.deliveryMethod || "N/A"}</p>
              </div>
            </div>
            {service.deliveryMethod === "delivery" && service.deliveryAddress && (
              <div>
                <h4 className="text-sm font-medium text-gray-500">Delivery Address</h4>
                <p className="mt-1 text-sm text-gray-900 whitespace-pre-line">
                  {typeof service.deliveryAddress === "object"
                    ? [service.deliveryAddress.building, service.deliveryAddress.street, service.deliveryAddress.area, service.deliveryAddress.city, service.deliveryAddress.emirate, service.deliveryAddress.poBox && `P.O. Box ${service.deliveryAddress.poBox}`].filter(Boolean).join(", ")
                    : service.deliveryAddress}
                </p>
              </div>
            )}
            {service.deliveryMethod === "pickup" && service.pickupLocation && (
              <div>
                <h4 className="text-sm font-medium text-gray-500">Pickup Location</h4>
                <p className="mt-1 text-sm text-gray-900">{service.pickupLocation}</p>
              </div>
            )}
          </div>
        );

      default:
        // Handle Visa Application service types (e.g. "Visa Application - Investor", "Visa Application - Golden")
        if (service.serviceType?.startsWith("Visa Application")) {
          return renderVisaApplicationDetails();
        }
        return null;
    }
  };

  const renderVisaApplicationDetails = () => {
    const formData = service.formData || {};
    const pricing = service.pricingBreakdown || {};

    // Labels for formData keys
    const formFieldLabels = {
      applicationType: "Application Type",
      changeOfVisaStatus: "Change of Visa Status",
      medicalApplicationType: "Medical Application Type",
      period: "Visa Period",
      visaType: "Visa Type",
      useFreeVisa: "Using Free Visa",
      name: "Full Name",
      email: "Email",
      mobileNumber: "Mobile Number",
      officeNumber: "Office Number",
      homeCountryTelephones: "Home Country Phone",
      homeCountryAddress: "Home Country Address",
      uaeAddress: "UAE Address",
      familyName: "Family Name",
      fathersName: "Father's Name",
      mothersName: "Mother's Name",
      maritalStatus: "Marital Status",
      designation: "Designation",
      previousNationality: "Previous Nationality",
      presentNationality: "Present Nationality",
      religion: "Religion",
      qualification: "Qualification",
      hasEducationCert: "Has Education Certificate",
      quota: "Quota",
      quotaUsed: "Quota Used",
    };

    return (
      <div className="space-y-6">
        {/* Visa Overview */}
        <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
          {service.visaType && (
            <div>
              <h4 className="text-sm font-medium text-gray-500">Visa Type</h4>
              <p className="mt-1 text-sm text-gray-900 capitalize">
                {service.visaType.replace(/-/g, " ")}
              </p>
            </div>
          )}
          {service.jurisdiction && (
            <div>
              <h4 className="text-sm font-medium text-gray-500">Jurisdiction</h4>
              <p className="mt-1 text-sm text-gray-900">{service.jurisdiction}</p>
            </div>
          )}
          {service.companyType && (
            <div>
              <h4 className="text-sm font-medium text-gray-500">Company Type</h4>
              <p className="mt-1 text-sm text-gray-900">{service.companyType}</p>
            </div>
          )}
          {service.totalPrice != null && (
            <div>
              <h4 className="text-sm font-medium text-gray-500">Total Price</h4>
              <p className="mt-1 text-sm font-semibold text-blue-600">
                AED {Number(service.totalPrice).toLocaleString()}
              </p>
            </div>
          )}
        </div>

        {/* Applicant Information from formData */}
        {Object.keys(formData).length > 0 && (
          <div>
            <h4 className="text-base font-medium text-gray-900 mb-3 flex items-center">
              <User className="h-5 w-5 mr-2" />
              Applicant Information
            </h4>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {Object.entries(formData).map(([key, value]) => {
                // Skip empty values
                if (value === "" || value === null || value === undefined) return null;

                const label = formFieldLabels[key] || key.replace(/([A-Z])/g, " $1").replace(/^./, (s) => s.toUpperCase());
                let displayValue;

                if (typeof value === "boolean") {
                  displayValue = value ? "Yes" : "No";
                } else if (typeof value === "number") {
                  displayValue = String(value);
                } else {
                  displayValue = String(value);
                }

                return (
                  <div key={key}>
                    <h5 className="text-sm font-medium text-gray-500">{label}</h5>
                    <p className="mt-1 text-sm text-gray-900">{displayValue}</p>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {/* Pricing Breakdown */}
        {pricing.items && pricing.items.length > 0 && (
          <div className="bg-gray-50 rounded-lg p-4">
            <h4 className="text-base font-medium text-gray-900 mb-3 flex items-center">
              <CreditCard className="h-5 w-5 mr-2" />
              Pricing Breakdown
            </h4>
            <div className="space-y-2">
              {pricing.items.map((item, index) => (
                <div
                  key={index}
                  className="flex items-center justify-between text-sm"
                >
                  <span className="text-gray-600">
                    {item.name}
                    {item.note && (
                      <span className="text-xs text-gray-400 ml-1">
                        ({item.note})
                      </span>
                    )}
                  </span>
                  <span className="font-medium text-gray-900">
                    AED {Number(item.amount).toLocaleString()}
                  </span>
                </div>
              ))}
              <div className="border-t pt-2 mt-2">
                {pricing.subtotal != null && (
                  <div className="flex justify-between text-sm">
                    <span className="text-gray-600">Subtotal</span>
                    <span className="font-medium text-gray-900">
                      AED {Number(pricing.subtotal).toLocaleString()}
                    </span>
                  </div>
                )}
                {pricing.vat != null && pricing.vat > 0 && (
                  <div className="flex justify-between text-sm">
                    <span className="text-gray-600">VAT</span>
                    <span className="font-medium text-gray-900">
                      AED {Number(pricing.vat).toLocaleString()}
                    </span>
                  </div>
                )}
                <div className="flex justify-between text-sm font-bold mt-1">
                  <span className="text-gray-900">Total</span>
                  <span className="text-blue-600">
                    AED {Number(pricing.total).toLocaleString()}
                  </span>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    );
  };

  const renderRequesterInfo = () => {
    if (!service?.userDetails) return null;

    return (
      <div className="bg-white shadow rounded-lg p-6">
        <h3 className="text-lg font-medium text-gray-900 mb-4">Requester Information</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div className="flex items-center space-x-3">
            <User className="h-5 w-5 text-gray-400" />
            <div>
              <p className="text-sm font-medium text-gray-500">Name</p>
              <p className="text-sm text-gray-900">
                {service.userDetails.first_name} {service.userDetails.last_name}
              </p>
            </div>
          </div>
          <div className="flex items-center space-x-3">
            <Mail className="h-5 w-5 text-gray-400" />
            <div>
              <p className="text-sm font-medium text-gray-500">Email</p>
              <p className="text-sm text-gray-900">{service.userDetails.email}</p>
            </div>
          </div>
          {service.userDetails.phone_number && (
            <div className="flex items-center space-x-3">
              <Phone className="h-5 w-5 text-gray-400" />
              <div>
                <p className="text-sm font-medium text-gray-500">Phone</p>
                <p className="text-sm text-gray-900">{service.userDetails.phone_number}</p>
              </div>
            </div>
          )}
        </div>
      </div>
    );
  };

  const renderDocuments = () => {
    try {
      // Use robust document handler
      const processedDocs = documentHandler.processDocuments(service?.documents, service?.serviceType);
      
      if (processedDocs.length === 0) {
        return (
          <div className="text-center py-12">
            <FileText className="mx-auto h-12 w-12 text-gray-400" />
            <h3 className="mt-2 text-sm font-medium text-gray-900">No documents</h3>
            <p className="mt-1 text-sm text-gray-500">
              No documents have been uploaded for this service
            </p>
          </div>
        );
      }

      // Handle Legal Translation documents with enhanced display
      if (service?.serviceType === "Legal Translation") {
        return (
          <div className="space-y-4">
            {/* Document Statistics */}
            <div className="bg-blue-50 rounded-lg p-4">
              <h4 className="text-sm font-medium text-blue-900 mb-2">Document Summary</h4>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-xs text-blue-700">
                <div>
                  <span className="font-medium">Total:</span> {processedDocs.length}
                </div>
                <div>
                  <span className="font-medium">With URLs:</span> {processedDocs.filter(doc => doc.isValid).length}
                </div>
                <div>
                  <span className="font-medium">Total Words:</span> {processedDocs.reduce((sum, doc) => sum + doc.words, 0).toLocaleString()}
                </div>
                <div>
                  <span className="font-medium">Total Cost:</span> AED {processedDocs.reduce((sum, doc) => sum + doc.cost, 0).toLocaleString()}
                </div>
              </div>
            </div>

            {/* Individual Documents */}
            <div className="grid grid-cols-1 gap-4">
              {processedDocs.map((docData, index) => {
                const formattedDoc = documentHandler.formatDocumentForDisplay(docData);
                
                return (
                  <div key={index} className={`p-4 border rounded-lg ${
                    formattedDoc.status.isComplete ? 'bg-green-50 border-green-200' : 
                    formattedDoc.status.hasValidUrl ? 'bg-blue-50 border-blue-200' : 
                    'bg-gray-50 border-gray-200'
                  }`}>
                    <div className="flex items-center justify-between mb-2">
                      <div className="flex items-center">
                        <FileText className={`h-6 w-6 ${
                          formattedDoc.status.isComplete ? 'text-green-600' : 
                          formattedDoc.status.hasValidUrl ? 'text-blue-600' : 
                          'text-gray-400'
                        }`} />
                        <span className="ml-3 flex-1 text-sm font-medium text-gray-900">
                          {formattedDoc.displayName}
                        </span>
                        {formattedDoc.error && (
                          <span className="text-xs text-red-600 ml-2">(Error)</span>
                        )}
                      </div>
                      {formattedDoc.status.hasValidUrl ? (
                        <a
                          href={formattedDoc.displayUrl}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="flex items-center px-3 py-1 text-xs font-medium text-blue-600 bg-blue-50 rounded-md hover:bg-blue-100"
                        >
                          <Download className="h-4 w-4 mr-1" />
                          View
                        </a>
                      ) : (
                        <span className="text-xs text-gray-500 px-3 py-1">
                          No URL
                        </span>
                      )}
                    </div>
                    <div className="grid grid-cols-3 gap-4 text-xs text-gray-600">
                      <div>
                        <span className="font-medium">Language:</span> {formattedDoc.displayLanguage}
                      </div>
                      <div>
                        <span className="font-medium">Words:</span> {formattedDoc.displayWords}
                      </div>
                      <div>
                        <span className="font-medium">Cost:</span> {formattedDoc.displayCost}
                      </div>
                    </div>
                    {formattedDoc.error && (
                      <div className="mt-2 text-xs text-red-600">
                        Error: {formattedDoc.error}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        );
      }

      // Handle other service types with robust error handling
      return (
        <div className="grid grid-cols-1 gap-4">
          {processedDocs.map((docData, index) => {
            const formattedDoc = documentHandler.formatDocumentForDisplay(docData);
            
            if (formattedDoc.status.hasValidUrl) {
              return (
                <a
                  key={index}
                  href={formattedDoc.displayUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center p-4 border rounded-lg hover:bg-gray-50 transition-colors"
                >
                  <FileText className="h-6 w-6 text-gray-400" />
                  <span className="ml-3 flex-1 text-sm text-gray-900">{formattedDoc.displayName}</span>
                  <Download className="h-5 w-5 text-gray-400" />
                </a>
              );
            } else {
              return (
                <div key={index} className="flex items-center p-4 border rounded-lg bg-gray-50">
                  <FileText className="h-6 w-6 text-gray-400" />
                  <span className="ml-3 flex-1 text-sm text-gray-900">{formattedDoc.displayName}</span>
                  <span className="text-xs text-gray-500">No URL</span>
                </div>
              );
            }
          })}
        </div>
      );
    } catch (error) {
      console.error('Error rendering documents:', error);
      return (
        <div className="text-center py-12">
          <FileText className="mx-auto h-12 w-12 text-red-400" />
          <h3 className="mt-2 text-sm font-medium text-gray-900">Error loading documents</h3>
          <p className="mt-1 text-sm text-gray-500">
            There was an error processing the document data
          </p>
        </div>
      );
    }
  };

  const getBackButtonDestination = () => {
    if (id) {
      return `/companies/${id}`;
    } else {
      // If no company ID, go back to dashboard
      return `/companies`;
    }
  };

  const renderBackButton = () => {
    const destination = getBackButtonDestination();
    const label = id ? "Back to Company" : "Back to Dashboard";

    return (
      <button
        onClick={() => navigate(destination)}
        className="inline-flex items-center text-sm text-gray-500 hover:text-gray-700"
      >
        <ArrowLeft className="w-4 h-4 mr-2" />
        {label}
      </button>
    );
  };

  if (loading) {
    return (
      <div className="min-h-screen flex justify-center items-center bg-gray-50">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600"></div>
      </div>
    );
  }

  const ServiceIcon = service ? getServiceIcon(service.serviceType) : BriefcaseIcon;

  return (
    <div className="min-h-screen bg-gray-50">
      <Header />
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Back button and notification */}
        <div className="space-y-4 mb-6">
          {renderBackButton()}

          {notification.show && (
            <div
              className={`p-4 rounded-md ${
                notification.type === "success"
                  ? "bg-green-50 text-green-800"
                  : "bg-red-50 text-red-800"
              }`}
            >
              {notification.message}
            </div>
          )}
        </div>

        <div className="space-y-6">
          {/* Service Overview Card */}
          <div className="bg-white shadow rounded-lg p-6">
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-4">
                <div className="h-12 w-12 rounded-full bg-indigo-100 flex items-center justify-center">
                  <ServiceIcon className="h-6 w-6 text-indigo-600" />
                </div>
                <div>
                  <h1 className="text-2xl font-bold text-gray-900">
                    {service?.serviceType}
                  </h1>
                  <div className="flex items-center mt-1 space-x-4">
                    <div className="flex items-center text-sm text-gray-500">
                      <Clock className="w-4 h-4 mr-1" />
                      Requested on {new Date(service?.requestDate).toLocaleDateString()}
                    </div>
                    <ServiceStatusBadge status={service?.status} />
                    {service?.paymentStatus && (
                      <span
                        className={`px-2.5 py-0.5 rounded-full text-xs font-medium ${
                          service.paymentStatus === "complete"
                            ? "bg-green-100 text-green-800"
                            : "bg-yellow-100 text-yellow-800"
                        }`}
                      >
                        {service.paymentStatus}
                      </span>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Service Status Update */}
          <ServiceStatusUpdate service={service} onStatusUpdate={handleStatusUpdate} />

          {/* Main Content Grid */}
          <div className="grid grid-cols-1 gap-6">
            {/* Service Details Card */}
            <div className="bg-white shadow rounded-lg p-6">
              <h3 className="text-lg font-medium text-gray-900 mb-4">
                Service Details
              </h3>
              {renderServiceSpecificDetails()}
            </div>

            {/* Company Information (if available) */}
            {service?.companyDetails && (
              <div className="bg-white shadow rounded-lg p-6">
                <h3 className="text-lg font-medium text-gray-900 mb-4">
                  Company Information
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <h4 className="text-sm font-medium text-gray-500">Company Name</h4>
                    <p className="mt-1 text-sm text-gray-900">
                      {service.companyDetails.companyNames?.first?.english || "N/A"}
                    </p>
                  </div>
                  <div>
                    <h4 className="text-sm font-medium text-gray-500">Status</h4>
                    <p className="mt-1 text-sm text-gray-900 capitalize">
                      {service.companyDetails.status || "N/A"}
                    </p>
                  </div>
                </div>
              </div>
            )}

            {/* Requester Information */}
            {renderRequesterInfo()}

            {/* Documents Section */}
            <div className="bg-white shadow rounded-lg p-6">
              <h3 className="text-lg font-medium text-gray-900 mb-4">
                Documents
              </h3>
              {renderDocuments()}
            </div>

            {/* Document Upload Section */}
            <div className="bg-white shadow rounded-lg p-6">
              <h3 className="text-lg font-medium text-gray-900 mb-4">
                Document Management
              </h3>
              <ServiceDocumentUploader
                serviceId={serviceId}
                onDocumentUpload={handleDocumentUpload}
                onDocumentDelete={handleDocumentDelete}
                onStatusUpdate={handleServiceStatusUpdate}
              />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ServiceDetailView;